/**
 * moduleTracker — mäter hur KanPlan används, för Modulstatistik.
 *
 * Vad som skickas till /api/module-analytics/events:
 *   pageview  när en ny vy öppnas (modul, vy, projektflik)
 *   active    uppmätt aktiv tid för vyn, i sekunder
 *
 * Aktiv tid räknas bara när fliken syns OCH personen varit aktiv (klick,
 * tangent, scroll, rörelse) de senaste 5 minuterna. En flik som står öppen i
 * bakgrunden, eller en skärm man gått ifrån, räknas alltså inte. Tiden
 * skickas när vyn byts, när fliken döljs eller stängs, och var femte minut
 * under långa pass — så att en stängd flik inte längre tappar hela besöket
 * (v1 fick tid för ungefär 6 % av besöken).
 *
 * Session = ett arbetspass. Efter 30 minuters uppehåll börjar en ny, så
 * att "besök" i statistiken inte blåses upp av varje flikbyte.
 *
 * Allt är best-effort: ett misslyckat anrop får aldrig störa användaren.
 */
import { API_URL } from '../api';

const SESSION_NYCKEL = 'kanplan-analytics-session';
const SESSION_UPPEHALL_MS = 30 * 60 * 1000;
const INAKTIV_MS = 5 * 60 * 1000;
const TICK_MS = 15 * 1000;
const SKICKA_EFTER_SEK = 300;

let aktuell = null; // { vy, kalla, nyckel }
let ackumulerat = 0;
let senasteTick = Date.now();
let senasteAktivitet = Date.now();
let lyssnarPa = false;
let reservSession = null;

function nyttId() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (_) { /* fall igenom */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function sessionId() {
  const nu = Date.now();
  try {
    const raw = window.sessionStorage.getItem(SESSION_NYCKEL);
    const s = raw ? JSON.parse(raw) : null;
    const id = s && s.id && nu - (s.senast || 0) < SESSION_UPPEHALL_MS ? s.id : nyttId();
    window.sessionStorage.setItem(SESSION_NYCKEL, JSON.stringify({ id, senast: nu }));
    return id;
  } catch (_) {
    if (!reservSession || nu - reservSession.senast >= SESSION_UPPEHALL_MS) reservSession = { id: nyttId(), senast: nu };
    reservSession.senast = nu;
    return reservSession.id;
  }
}

function lasLokalt(nyckel) {
  try { return (window.localStorage.getItem(nyckel) || '').trim(); } catch (_) { return ''; }
}

export function vyNyckel(vy) {
  if (!vy) return '';
  return [vy.moduleKey, vy.vy || '', vy.flik || '', vy.projectId || ''].join('|');
}

function skicka(vy, kalla, typ, sekunder) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const slug = lasLokalt('kanplan-active-tenant-slug');
    if (slug) headers['X-Tenant-Slug'] = slug;
    const simulerad = !!(lasLokalt('kanplan-simulated-user') || lasLokalt('kanplan-simulated-role'));
    const metadata = { session_id: sessionId(), source: kalla };
    if (vy.vy) metadata.vy = vy.vy;
    if (vy.flik) metadata.flik = vy.flik;
    if (vy.projectId) metadata.project_id = vy.projectId;
    if (simulerad) metadata.simulated = true;
    const body = { module_key: vy.moduleKey, event_type: typ, metadata };
    if (sekunder) body.duration_seconds = sekunder;
    fetch(`${API_URL}/api/module-analytics/events`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers,
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch (_) { /* best-effort */ }
}

function synlig() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function tick() {
  const nu = Date.now();
  const delta = Math.min(nu - senasteTick, TICK_MS * 2) / 1000;
  senasteTick = nu;
  if (aktuell && synlig() && nu - senasteAktivitet < INAKTIV_MS) ackumulerat += delta;
  if (ackumulerat >= SKICKA_EFTER_SEK) skickaTid();
}

function skickaTid() {
  const sek = Math.round(ackumulerat);
  ackumulerat = 0;
  if (aktuell && sek >= 1) skicka(aktuell.vy, aktuell.kalla, 'active', sek);
}

function aktivitet() {
  senasteAktivitet = Date.now();
}

function startaLyssnare() {
  if (lyssnarPa || typeof window === 'undefined') return;
  lyssnarPa = true;
  const opt = { passive: true, capture: true };
  ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll', 'mousemove'].forEach((t) => window.addEventListener(t, aktivitet, opt));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      tick();
      skickaTid();
    } else {
      // Den dolda tiden ska inte räknas in vid nästa tick.
      senasteTick = Date.now();
      senasteAktivitet = Date.now();
    }
  });
  window.addEventListener('pagehide', () => { tick(); skickaTid(); });
  window.setInterval(tick, TICK_MS);
}

/**
 * Sätt vyn som användaren tittar på just nu (null = ingen mätbar vy).
 * @param {{moduleKey: string, vy?: string|null, flik?: string|null, projectId?: number|null}|null} vy
 * @param {'web'|'field'} kalla
 */
export function sattVy(vy, kalla = 'web') {
  tick();
  const nyckel = vyNyckel(vy);
  if (aktuell && aktuell.nyckel === nyckel) return;
  skickaTid();
  aktuell = vy ? { vy, kalla, nyckel } : null;
  senasteTick = Date.now();
  senasteAktivitet = Date.now();
  if (aktuell) {
    skicka(vy, kalla, 'pageview');
    startaLyssnare();
  }
}
